import { v4 as uuidv4 } from 'uuid';

import authService from '../../authentication/auth.service';
import deviceService from '../../authentication/device.service';
import teamSwitcherService from '../../team/teamSwitcher.service';
import type { ReceiveSocketEventBasePayload, SendSocketEventBasePayload } from './socket.types';
import type { SendSocketAuthEventPayloadMap } from './socketEvent.types';
import { ReceiveSocketAuthEvents, SendSocketAuthEvents } from './socketEvent.types';
import { SocketHandler } from './socketHandler.class';

const MAX_RQUID_BUFFER = 250;
const PREVIOUS_RQUIDS_MAX_BOUNDS = 200;
const PREVIOUS_RQUIDS_MAX_BOUNDS_SLICE_SIZE = 20;

interface SocketAuthParams {
  adminToken: string | null;
  authToken: string | null;
  deviceToken: string | null;
}

type SubscriberHandler<Payload extends ReceiveSocketEventBasePayload> = (payload: Payload) => void;

interface SubscriberConfig<Payload extends ReceiveSocketEventBasePayload> {
  handler: SubscriberHandler<Payload>;
  skipOwn: boolean;
  restrictToOwn: boolean;
}

type MapValueType<T> = T[keyof T];

export class SocketEventHub<
  SendSocketEventPayloadMap extends Record<string, SendSocketEventBasePayload>,
  ReceiveSocketEventPayloadMap extends Record<string, ReceiveSocketEventBasePayload>,
> {
  private onceOnlyEvents: Array<keyof ReceiveSocketEventPayloadMap>;
  private retryTimeout?: NodeJS.Timeout;
  private inProgress = false;
  private rquids: Array<string> = [];
  private socketHandler?: SocketHandler<
    MapValueType<SendSocketEventPayloadMap>,
    MapValueType<ReceiveSocketEventPayloadMap>
  >;
  // event name -> bindId -> subscriber config (handler)
  private subscribers: Record<
    keyof ReceiveSocketEventPayloadMap,
    undefined | Record<string, SubscriberConfig<MapValueType<ReceiveSocketEventPayloadMap>>>
  >;
  private deduplicatedEvents = new Map<keyof ReceiveSocketEventPayloadMap, Array<string>>();

  /**
   * @param onceOnlyEvents Events that will be de-duplicated automatically, defined by `rquid`.
   */
  constructor(onceOnlyEvents: Array<keyof ReceiveSocketEventPayloadMap>) {
    this.onceOnlyEvents = onceOnlyEvents;
    this.subscribers = {} as any;
  }

  /**
   * Used by the interceptor to de-duplicate subscribed handler executions.
   */
  public setInProgress(inProgress: boolean) {
    this.inProgress = inProgress;
  }

  /**
   * Used by the interceptor to register rquids to de-duplicate subscribed handler executions.
   */
  public setRquid(rquid: string) {
    if (!rquid) {
      return;
    }

    this.rquids.push(rquid);

    // Keep the list reasonably sized
    if (this.rquids.length > MAX_RQUID_BUFFER) {
      this.rquids.shift();
    }
  }

  /**
   * Open a socket connection if one does not already exist.
   */
  public async start() {
    const { adminAccessToken: adminToken, authToken } = authService.getCurrentTokens();
    const deviceToken = deviceService.getDeviceToken();
    const teamId = teamSwitcherService.getCurrentTeam()?.id;

    if (!authToken || !deviceToken) {
      clearTimeout(this.retryTimeout);
      this.retryTimeout = setTimeout(async () => {
        await this.start();
      }, 2000);
      return;
    }

    if (this.socketHandler) {
      // eslint-disable-next-line no-console
      console.warn('Socket connection already established');
      return;
    }

    this.socketHandler = new SocketHandler<
      MapValueType<SendSocketEventPayloadMap>,
      MapValueType<ReceiveSocketEventPayloadMap>
    >(this.handleSocketEvent.bind(this, { adminToken, authToken, deviceToken, teamId }));
    this.socketHandler.open();
  }

  /**
   * Close the socket connection.
   */
  public stop() {
    this.socketHandler?.close();
    this.socketHandler = undefined;
  }

  /**
   * Register a `handler` to be executed when the specified `event` occurs.
   */
  public on<Name extends keyof ReceiveSocketEventPayloadMap, EventPayload extends ReceiveSocketEventPayloadMap[Name]>(
    eventName: Name,
    handler: SubscriberHandler<EventPayload>,
    skipOwn = true,
    restrictToOwn = false,
  ) {
    const bindId = uuidv4();

    this.subscribers[eventName] = {
      ...this.subscribers[eventName],
      [bindId]: { handler, skipOwn, restrictToOwn },
    };

    return {
      unsubscribe: () => {
        delete this.subscribers[eventName]?.[bindId];
      },
    };
  }

  private async handleSocketEvent(
    { authToken, adminToken, deviceToken }: SocketAuthParams,
    payload: MapValueType<ReceiveSocketEventPayloadMap>,
  ) {
    switch (payload.event) {
      case ReceiveSocketAuthEvents.AUTHORIZATION_REQUEST: {
        const authResponsePayload: SendSocketAuthEventPayloadMap[SendSocketAuthEvents.AUTHORIZATION_RESPONSE] = {
          event: SendSocketAuthEvents.AUTHORIZATION_RESPONSE,
          authToken,
          adminToken,
          deviceToken,
          deviceType: 'SineWeb',
          data: {},
        };

        const team = teamSwitcherService.getCurrentTeam();
        if (team) {
          authResponsePayload.teamId = team.id;
        }

        this.send(authResponsePayload);
        break;
      }
      case ReceiveSocketAuthEvents.AUTHORIZATION_SUCCESS: {
        break;
      }
      case ReceiveSocketAuthEvents.AUTHORIZATION_ERROR: {
        // eslint-disable-next-line no-console
        console.error('Socket authorization error', payload);
        this.stop();
        break;
      }
      case ReceiveSocketAuthEvents.TOKEN_EXPIRED_CLOSED:
      case ReceiveSocketAuthEvents.TOKEN_EXPIRED_ERROR: {
        console.log('Token expired, refreshing...');
        this.stop();
        await authService.refresh();
        await this.start();
        break;
      }
      default: {
        this.handleMessage(payload);
        break;
      }
    }
  }

  public sendMessage(
    name: keyof SendSocketEventPayloadMap,
    data: SendSocketEventPayloadMap[keyof SendSocketEventPayloadMap]['data'],
  ): void {
    // It's complaining about a very specific edge case, which is a non-issue with this amount of strict typing.
    return this.send({ event: name, data } as SendSocketEventPayloadMap[keyof SendSocketEventPayloadMap]);
  }

  private handleMessage(payload: MapValueType<ReceiveSocketEventPayloadMap>) {
    // De-duplicate
    if (this.onceOnlyEvents.some((name) => payload.event === name)) {
      let previousRquids = this.deduplicatedEvents.get(payload.event) || [];

      if (previousRquids.includes(payload.rquid)) {
        // Short-circuit early as only want to execute this
        // ONCE per unique rquid
        return;
      }

      previousRquids.push(payload.rquid);

      // Keep the list reasonably sized
      if (previousRquids.length >= PREVIOUS_RQUIDS_MAX_BOUNDS) {
        previousRquids = previousRquids.slice(PREVIOUS_RQUIDS_MAX_BOUNDS_SLICE_SIZE);
      }

      this.deduplicatedEvents.set(payload.event, previousRquids);
    }

    const handlerConfigs = Object.values(this.subscribers[payload.event] || {});

    if (!handlerConfigs.length) {
      return;
    }

    const deviceToken = deviceService.getDeviceToken();

    handlerConfigs.forEach(({ handler, skipOwn, restrictToOwn }) => {
      const isOwn = payload.source === deviceToken;
      const isSystemCall = payload.source === 'systemCall';

      // HACK: timeout required
      // latency means web push arrives before API call finishes
      // let's hope the timeout is high enough
      const waitTime = (isOwn || isSystemCall) && this.inProgress ? 2000 : 0;

      setTimeout(() => {
        if (restrictToOwn && !isOwn) {
          return;
        }

        const ownRquid = this.rquids.includes(payload.rquid);

        // During impersonation, the server emits events via asSystem() which
        // replaces the device token with 'systemCall', causing isOwn to be false.
        // In that case, fall back to rquid-only matching since the rquid is still
        // the same one from our HTTP request.
        if (skipOwn && (isOwn || isSystemCall) && ownRquid) {
          return;
        }

        handler(payload);
      }, waitTime);
    });
  }

  private send(
    payload: Parameters<
      SocketHandler<MapValueType<SendSocketEventPayloadMap>, MapValueType<ReceiveSocketEventPayloadMap>>['send']
    >[0],
  ) {
    if (!this.socketHandler) {
      // eslint-disable-next-line no-console
      console.error('Socket not open, cannot send message', { payload });
      return;
    }

    this.socketHandler.send(payload);
  }
}
